import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import * as d3 from "d3";
import { AxisBottom, AxisLeft } from "Atoms/Charts/Scales";
import { Bars, StackedBars, GroupedBars } from "Atoms/Charts/Bars";
import { Tooltip } from "Atoms/Charts/Tooltip";
import { defaultStyles, mergeStyles } from "./styles";

function BarChart({
  data,
  width,
  height,
  styles: styleOverrides,
  type,
  series,
  xMaxOverride,
}) {
  const styles = useMemo(
    () => mergeStyles(defaultStyles, styleOverrides),
    [styleOverrides]
  );
  const { margin, animation, bar } = styles;

  const tooltipRef = useRef(null);
  const [activeBar, setActiveBar] = useState("");
  const [activeSegment, setActiveSegment] = useState("");
  const [animated, setAnimated] = useState(!animation.enabled);

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  /*Quiero que al menos bandwidth =  minHeight
    scaleBand: 
    n = numero de bars
    step = range / (n + padding)
    bandwidth = step × (1 - padding)
    ---------
    minHeight = range × (1 - padding) / (n + padding)
    range = minHeight × (n + padding) / (1 - padding)
  */
  const scaleFactor = (data.length + bar.padding) / (1 - bar.padding);
  const minRange = bar.minHeight * scaleFactor;
  const maxRange = bar.maxHeight * scaleFactor;
  const svgHeight = Math.max(minRange, Math.min(innerHeight, maxRange));

  const xMaxDomain = useMemo(
    () =>
      xMaxOverride ??
      (type === "stacked"
        ? d3.max(data, (d) => d3.sum(d.values))
        : d3.max(data, (d) => d3.max(d.values))),
    [data, type, xMaxOverride]
  );

  const xScale = useMemo(
    () =>
      d3.scaleLinear().domain([0, xMaxDomain]).nice().range([0, innerWidth]),
    [data, innerWidth]
  );

  const yScale = useMemo(
    () =>
      d3
        .scaleBand()
        .domain(data.map((d) => d.label))
        .range([0, svgHeight])
        .padding(bar.padding),
    [data, svgHeight]
  );

  // Activar animación al montar
  useEffect(() => {
    if (animation.enabled) {
      const timer = setTimeout(() => setAnimated(true), 100);
      return () => clearTimeout(timer);
    }
  }, [animation.enabled]);

  const handleBarEnter = useCallback((d, e) => {
    tooltipRef.current?.show({
      label: d.label,
      value: d.v ?? d.values[0],
      segment: d.segment,
      x: e.clientX,
      y: e.clientY,
    });
    setActiveBar(d.label);
    setActiveSegment(d.segment?.name ?? "");
  }, []);

  const handleBarMove = useCallback((d, e) => {
    tooltipRef.current?.move(e.clientX, e.clientY);
  }, []);

  const handleBarLeave = useCallback(() => {
    tooltipRef.current?.hide();
    setActiveBar("");
    setActiveSegment("");
  }, []);

  return (
    <>
      <div
        style={{
          position: "relative",
          width,
          height,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <svg
          width={width}
          height={svgHeight + margin.top}
          style={{ display: "block" }}
        >
          <g transform={`translate(${margin.left},${margin.top})`}>
            <AxisLeft scale={yScale} styles={styles.axisY} />
            {type === "stacked" ? (
              <StackedBars
                data={data}
                xScale={xScale}
                yScale={yScale}
                styles={bar}
                animation={animation}
                animated={animated}
                activeBar={activeBar}
                activeSegment={activeSegment}
                onBarEnter={handleBarEnter}
                onBarMove={handleBarMove}
                onBarLeave={handleBarLeave}
                series={series}
              />
            ) : type === "grouped" ? (
              <GroupedBars
                data={data}
                xScale={xScale}
                yScale={yScale}
                styles={bar}
                animation={animation}
                animated={animated}
                activeBar={activeBar}
                activeSegment={activeSegment}
                onBarEnter={handleBarEnter}
                onBarMove={handleBarMove}
                onBarLeave={handleBarLeave}
                series={series}
              />
            ) : (
              <Bars
                data={data}
                xScale={xScale}
                yScale={yScale}
                styles={bar}
                animation={animation}
                animated={animated}
                activeBar={activeBar}
                onBarEnter={handleBarEnter}
                onBarMove={handleBarMove}
                onBarLeave={handleBarLeave}
              />
            )}
          </g>
        </svg>
        <svg
          width={width}
          height={margin.bottom}
          style={{
            display: "block",
            position: "sticky",
            bottom: 0,
            left: 0,
            background: "white",
          }}
        >
          <g transform={`translate(${margin.left},0)`}>
            <AxisBottom
              scale={xScale}
              transform={`translate(0,0)`}
              styles={styles.axisX}
            />
          </g>
        </svg>
      </div>
      <Tooltip ref={tooltipRef} styles={styles.tooltip} />
    </>
  );
}

export { BarChart };
export default BarChart;
